import { useMutation } from '@tanstack/react-query';
import { moveQuoteV2ItemsMutation } from 'Services/Mutations/QuotesV2';
import { useQuoteCache } from './use-quote-cache';

export interface ReorderMove {
  itemId: string;
  position: string;
  targetSectionId: string | null;
}

interface ReorderItemsVariables {
  moves: ReorderMove[];
}

/**
 * Reorder items via V2 move endpoint with fractional indexing.
 * Only sends changed positions instead of replacing the full items array.
 */
export const useReorderItems = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = moveQuoteV2ItemsMutation();

  return useMutation({
    mutationFn: ({ moves }: ReorderItemsVariables) =>
      mutationFn({ quoteId, moves }),

    onMutate: async () => {
      await cache.cancel();

      const previousQuote = cache.getQuote();
      const previousItems = cache.getItems();

      return { previousQuote, previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: () => {
      cache.refetch();
    }
  });
};
